import { ShieldAlert, Skull, Clock, ExternalLink } from "lucide-react";
import { fmtDate } from "../lib/utils";

const TIER_STYLE = {
  EMERGENCY: {
    bg: "bg-red-50",
    border: "border-red-500",
    text: "text-red-700",
    bar: "#dc2626",
    halo: "shadow-sm",
  },
  "PATCH NOW": {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    bar: "#ea580c",
    halo: "shadow-sm",
  },
  "THIS SPRINT": {
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-700",
    bar: "#ca8a04",
    halo: "",
  },
  BACKLOG: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    bar: "#475569",
    halo: "",
  },
  INFORMATIONAL: {
    bg: "bg-slate-100",
    border: "border-slate-200",
    text: "text-slate-600",
    bar: "#7c3aed",
    halo: "",
  },
};

function pctText(p) {
  if (p == null) return "-";
  const n = Math.round(p * 100);
  if (n === 100) return "100th";
  const last = n % 10;
  const lastTwo = n % 100;
  let suffix = "th";
  if (lastTwo < 11 || lastTwo > 13) {
    if (last === 1) suffix = "st";
    else if (last === 2) suffix = "nd";
    else if (last === 3) suffix = "rd";
  }
  return `${n}${suffix}`;
}

export default function PatchPriority({ ti }) {
  if (!ti) return null;
  const priority = ti.priority || {};
  const epss = ti.epss;
  const kev = ti.kev;
  const score = Number(priority.score ?? 0);
  const pct = Math.max(0, Math.min(100, score * 10));
  const tier = priority.tier || "INFORMATIONAL";
  const style = TIER_STYLE[tier] || TIER_STYLE.INFORMATIONAL;
  const ransomware =
    (kev?.knownRansomwareCampaignUse || "").toLowerCase() === "known";

  return (
    <section
      data-testid="patch-priority"
      className={`panel p-6 md:p-7 fade-up delay-1 relative overflow-hidden ${style.halo}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="overline flex items-center gap-2">
            <ShieldAlert className="h-3 w-3 text-violet-700" />
            Patch Priority · operational verdict
          </div>
          <h3 className="font-display text-xl text-slate-900 mt-1">
            Should I patch this <span className={style.text}>right now</span>?
          </h3>
        </div>

        <div
          data-testid="priority-tier"
          className={`border ${style.border} ${style.bg} ${style.text} rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em]`}
        >
          {tier}
        </div>
      </div>

      <RceBanner rce={priority.unauth_rce} />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-4">
          <div
            data-testid="priority-score"
            className="font-mono font-bold text-6xl md:text-7xl text-slate-900 tracking-tighter leading-none"
          >
            {score.toFixed(1)}
            <span className="text-slate-500 text-2xl">/10</span>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-700">
            <Clock className="h-3.5 w-3.5 text-violet-700" />
            <span>SLA:</span>
            <span className="font-mono text-slate-900">
              {priority.sla || "-"}
            </span>
          </div>
          {priority.legacy && (
            <div data-testid="priority-legacy" className="mt-2 text-[10px] font-mono text-slate-500">
              legacy composite {Number(priority.legacy.score ?? 0).toFixed(1)}/10 · {priority.legacy.tier}
            </div>
          )}
        </div>

        <div className="md:col-span-8">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
            <div
              className="h-full transition-all duration-700"
              style={{ width: `${pct}%`, background: style.bar }}
            />
          </div>

          <div className="grid grid-cols-3 gap-3" data-testid="priority-components">
            <SignalCell
              label="CVSS"
              value={priority.components?.cvss ?? "-"}
              suffix={priority.components?.cvss != null ? "/10" : ""}
              tone="text-slate-900"
            />
            <SignalCell
              label="EPSS percentile"
              value={pctText(epss?.percentile)}
              suffix=""
              tone={
                (epss?.percentile ?? 0) >= 0.95
                  ? "text-red-700"
                  : (epss?.percentile ?? 0) >= 0.5
                    ? "text-orange-700"
                    : "text-slate-900"
              }
              hint={
                epss
                  ? `${Math.round((epss.score || 0) * 10000) / 100}% prob.`
                  : "no data"
              }
            />
            <SignalCell
              label="CISA KEV"
              value={kev ? "Listed" : "Not listed"}
              tone={kev ? "text-red-700" : "text-slate-600"}
              hint={kev ? `Added ${fmtDate(kev.dateAdded)}` : ""}
              icon={
                ransomware ? (
                  <Skull className="h-3 w-3 text-red-700" />
                ) : null
              }
            />
          </div>
        </div>
      </div>

      {Array.isArray(priority.drivers) && priority.drivers.length > 0 && (
        <div className="mt-6 pt-5 border-t border-slate-100">
          <div className="overline mb-2">Why this score</div>
          <ul className="space-y-1.5" data-testid="priority-drivers">
            {priority.drivers.map((d, i) => (
              <li
                key={i}
                className="text-sm text-slate-700 flex gap-2 items-start"
              >
                <span className="text-violet-700 font-mono">•</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {kev && (
        <div
          className="mt-5 bg-red-50 border border-red-200 rounded-md p-4"
          data-testid="kev-card"
        >
          <div className="flex items-center gap-2 mb-2">
            {ransomware && <Skull className="h-3.5 w-3.5 text-red-700" />}
            <span className="overline text-red-700">
              {ransomware
                ? "CISA KEV · known ransomware use"
                : "CISA KEV catalog entry"}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs">
            <KevField label="Vendor" value={kev.vendorProject} />
            <KevField label="Product" value={kev.product} />
            <KevField label="Date Added" value={fmtDate(kev.dateAdded)} mono />
            <KevField label="Federal Due" value={fmtDate(kev.dueDate)} mono />
          </div>
          {kev.requiredAction && (
            <p className="text-xs text-slate-700 mt-3 leading-relaxed">
              <span className="overline mr-2">REQUIRED ACTION</span>
              {kev.requiredAction}
            </p>
          )}
        </div>
      )}

      {epss?.date && (
        <div className="mt-4 flex items-center justify-between text-[10px] font-mono text-slate-500">
          <span>
            EPSS scored {epss.date} · KEV catalog refreshed every 6h
          </span>
          <a
            href="https://www.first.org/epss/"
            target="_blank"
            rel="noreferrer"
            className="hover:text-violet-700 flex items-center gap-1"
          >
            EPSS docs <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </section>
  );
}

function SignalCell({ label, value, suffix = "", tone = "text-slate-900", hint, icon }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <div className="overline flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`font-mono font-bold text-lg mt-1 ${tone}`}>
        {value}
        {suffix && <span className="text-slate-500 text-xs ml-0.5">{suffix}</span>}
      </div>
      {hint && (
        <div className="text-[10px] font-mono text-slate-500 mt-0.5">{hint}</div>
      )}
    </div>
  );
}

function KevField({ label, value, mono }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div
        className={`text-slate-800 ${mono ? "font-mono" : ""} text-xs mt-0.5 break-words`}
      >
        {value || "-"}
      </div>
    </div>
  );
}

function RceBanner({ rce }) {
  if (!rce?.pattern) return null;
  return (
    <div
      data-testid="rce-override-banner"
      className="mb-5 border border-red-200 bg-red-50 rounded-md p-3"
    >
      <div className="text-xs font-bold uppercase tracking-wider text-red-700">
        {rce.confirmed ? "Confirmed" : "Probable"} unauthenticated RCE · emergency override
      </div>
      <ul className="mt-1.5 space-y-0.5 text-[11px] font-mono text-slate-700">
        {(rce.reasons || []).map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
      <div className="mt-1.5 text-[10px] text-slate-500">
        Raises patch urgency only. The technical exploitability score is unchanged.
      </div>
    </div>
  );
}
